/**
 * Copy for the LL33 card. Formatting only: every count, state and late day is
 * the server's (C7 contract §0, no FE math). Calendar days go through
 * `formatRecordDate` (Date.UTC parts), never `new Date("YYYY-MM-DD")`, which
 * renders Oct 19 for Oct 20 in New York.
 */
import {
  formatDay,
  formatRecordDate,
  nyClock,
  shiftIso,
} from "@/pages/Portal/ActionPlan/actionPlan";
import { formatUsdExact } from "@/components/portal/exposureAxes";
import type {
  BlockedOn,
  BuildingState,
  DateErrorCode,
  Ll33Card,
  Ll33Door,
  Ll33Photo,
  Ll33StatusLine,
  Ll33Step,
  NextAction,
  PartyRole,
  StepKey,
  StepState,
  Who,
} from "./useLl33";

export const LINK_BTN =
  "motion-tint inline-flex min-h-6 items-center text-[12px] text-dc-teal-ink underline underline-offset-2 hover:text-dc-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-dc-teal";

/** none: no LL33 UI (not In service, or no card); the one show rule. */
export const ll33View = (
  card: Ll33Card | null | undefined,
): "none" | "not_covered" | "live" => {
  if (!card?.inService) return "none";
  return !card.covered || card.statusLine.state === "not_covered"
    ? "not_covered"
    : "live";
};

export const WHO_LABEL: Record<Who, string> = {
  you: "You",
  codeclear: "CodeClear",
  super: "Super",
  dob: "DOB",
};

export const ROLE_LABEL: Record<PartyRole, string> = {
  super: "Super",
  community_manager: "Community manager",
  compliance_lead: "Compliance lead",
};

/** The one step-label table: tiles, the card's step list and its trail (attempt 3 one-word names). */
export const STEP_LABEL: Record<StepKey, string> = {
  authorize: "Authorize",
  supers: "Supers",
  labels: "Labels",
  doors: "Count",
  links: "Links",
  posted: "Post",
  deadline: "Deadline",
};

/** The sixth tile: no StepKey, it reads `totals.checking` (contract M9). */
export const CHECKED_LABEL = "Checked";

/** A step whose rows all wait (Labels on DOB), and the owner's undated step. */
export const BLOCKED = "Blocked";

/** Pattern A (Leo 10-17): verb first, 2-3 words, one wording on every surface. keep_posted is blank: Posted has nothing due. */
const ACTION_COPY: Record<NextAction, string> = {
  authorize: "Authorize",
  add_super: "Add super",
  download_labels: "Get label",
  count_doors: "Count doors",
  send_links: "Send links",
  post_photos: "Post photos",
  check_photos: "Check photos",
  keep_posted: "",
  none: "",
};

export const STATE_HEADLINE = {
  not_required: "Not required", // grade N posts nothing (Leo 10-16)
  waiting_on_count: "Waiting on count",
  count_low: "Count looks low",
  posted: "Posted",
  not_on_record: "Not on record",
} as const satisfies Partial<Record<BuildingState, string>>;
const HEADLINE: Partial<Record<BuildingState, string>> = STATE_HEADLINE;

/** Staff's act while the owner of record is checked (Ll33StatusRow checkOwner). */
export const CONFIRM_OWNER = "Confirm the owner of record";

/** While the Authorize click is off (counsel), the owner authorizes by email. */
export const AUTHORIZE_HOW = 'Reply "I authorize" to CodeClear\'s email.';

/** "3 of 7 doors posted": the one fraction, spoken; `short` "3 of 7" (the list's Doors). */
export const fractionText = (
  line: Pick<Ll33StatusLine, "posted" | "denominator">,
  short = false,
): string =>
  short
    ? `${line.posted} of ${line.denominator}`
    : `${line.posted} of ${line.denominator} door${line.denominator === 1 ? "" : "s"} posted`;

/** The served `blocked` (contract 1c): the owner's undated step (Leo 10-07 YOU BLOCKED) or a DOB wait. A line without the key throws. */
export const blockedOn = (line: Ll33StatusLine): BlockedOn | null => {
  if (line.blocked === undefined)
    throw new Error("LL33 status_line has no blocked");
  return line.blocked;
};

export const isBlocked = (line: Ll33StatusLine): boolean =>
  blockedOn(line) !== null;

/** The list's Low view and the pips' "Low" in place of p/N. */
export const LOW = "Low";

/** Badge beside the fraction. None while the owner is next; a late step never reads Waiting. */
export const stateHeadline = (line: Ll33StatusLine): string | null => {
  if (line.next.who === "you") return null;
  if (line.state === "waiting_on_count" && line.next.lateDays > 0) return null;
  return HEADLINE[line.state] ?? null;
};

export const plural = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

/** One kind of served pip on a line: Post's open doors, the estimate line, the doors bar and the Specimen. */
export const pipCount = (
  line: Pick<Ll33StatusLine, "pips">,
  pip: Ll33StatusLine["pips"][number],
): number => line.pips.filter((p) => p === pip).length;

/** An action's words: actionText's tail and Set dates' step buttons. */
export const actionWords = (action: NextAction): string => ACTION_COPY[action];

export const actionText = (line: Ll33StatusLine): string =>
  line.state === "count_low" && line.next.action === "count_doors"
    ? "Confirm count"
    : actionWords(line.next.action);

/** The served per-building fine; a payload without it throws, so no screen types the amount. */
export const fineText = (usd: number | null | undefined): string => {
  if (usd == null) throw new Error("LL33 payload has no per-building fine");
  return formatUsdExact(usd);
};

/** The one late rule: served late days above 0 (the list's Late view, the card's "N days late"). */
export const isLateBy = (lateDays: number): boolean => lateDays > 0;

export const STEP_STATE_WORD: Record<StepState, string> = {
  done: "done",
  due: "due",
  late: "late",
  waiting: "waiting",
  locked: "set by law",
};

/** The served door state's word; a not-posted door says its link (doorWord). */
const DOOR_WORD: Record<Exclude<Ll33Door["state"], "not_posted">, string> = {
  posted: "Posted",
  checking: "Checking",
  retired: "Removed",
};

/** A door with no photo once the posted step is late or the year is past; the tile reads it too. */
export const NOT_POSTED = "Not posted";
/** F27: a not-posted door with no live link (served state none, revoked or expired). */
export const NO_LINK = "No link yet";
/** F27 words by the served link reason (contract 1e). */
const LINK_WORD: Record<NonNullable<Ll33Door["link"]["reason"]>, string> = {
  first: "Link sent",
  change: "Link resent",
  reopen: "New link",
  retake: "Retake link sent",
};
/** Contract 1e: null on links issued before LINK_ISSUED carried a reason; read as the first send. */
const OLDER_LINK = "first";

/** "Link sent Oct 12, waiting": the served link, no date math. A live link without reason or day throws. */
const linkWord = (link: Ll33Door["link"], asOf: string): string => {
  if (link.state !== "active") return NO_LINK;
  if (link.reason === undefined || !link.issuedOn)
    throw new Error("LL33 door link has no reason or issued_on");
  return `${LINK_WORD[link.reason ?? OLDER_LINK]} ${dayLabel(link.issuedOn, asOf)}, waiting`;
};

/** A door's name: its label, else its street number. */
export const doorTitle = (door: Ll33Door): string =>
  door.label || door.streetNumber;

/** Door rows and tiles: the served state; a not-posted door says its link until the posted step is late or the year is past. */
export const doorWord = (door: Ll33Door, card: Ll33Card): string => {
  if (door.state !== "not_posted") return DOOR_WORD[door.state];
  const late =
    card.statusLine.state === "not_on_record" ||
    card.steps.some((s) => s.key === "posted" && s.state === "late");
  return late ? NOT_POSTED : linkWord(door.link, card.asOf);
};

/** F27: the served Labels step waiting on DOB, with its served next check; null otherwise. */
export const dobWaitText = (labels: Ll33Step, asOf: string): string | null => {
  if (labels.waitingOn !== "dob") return null;
  const next = labels.recheckOn
    ? ` Next check ${formatDay(labels.recheckOn, asOf)}.`
    : "";
  return `DOB has no grade yet.${next}`;
};

/** Numbers are the server's; none while sending is off. */
export const escalationText = (card: Ll33Card): string | null => {
  const r = card.reminders;
  if (
    !r ||
    ["posted", "not_on_record", "not_covered", "not_required"].includes(
      card.statusLine.state,
    )
  )
    return null;
  const cadence = `Reminder ${plural(r.daysBefore, "day")} before, then every ${plural(r.everyDays, "day")}. ${plural(r.cmAfterDays, "day")} late: community manager.`;
  const lead = card.escalation.find((e) => e.role === "compliance_lead");
  if (!lead) return cadence;
  const name =
    card.people.find((p) => p.role === "compliance_lead")?.name ||
    "compliance lead";
  return `${cadence} ${dayLabel(lead.on, card.asOf)}: ${name}.`;
};

type Label = Ll33Card["labels"][number];
/** A grade shows once its label is downloaded: the card's label line and the list's Grade. */
export const gradeShown = (
  l: Label,
): l is Label & { grade: string; downloadedOn: string } =>
  !!(l.grade && l.downloadedOn);

/** "Oct 13" this label year, "Oct 13, 2025" otherwise. */
export const dayLabel = (iso: string, asOf: string): string =>
  formatRecordDate(iso, asOf);

/** A served due date on a tile or a list row: "Today", else "Oct 21" (attempt 3 sday). */
export const dueLabel = (iso: string, asOf: string): string =>
  iso === asOf ? "Today" : dayLabel(iso, asOf);

const NY = "America/New_York";

/** "Oct 14, 2026, 9:12 AM EDT": EDT/EST so the Nov 1 01:30 fold reads as two times. */
export const instantLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: NY,
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: NY,
    timeZoneName: "short",
  });
  return `${date}, ${time}`;
};

/** "Sat 9:00 AM" when it is tomorrow in New York, else "Mon Nov 2 9:00 AM": no year, no zone (attempt 3 52_ll33.js:31). */
export const reminderLabel = (iso: string, asOf: string): string => {
  if (Number.isNaN(Date.parse(iso))) throw new Error(`Not an instant: ${iso}`);
  const { day, time } = nyClock(iso);
  const name = formatDay(day, asOf);
  return `${day === shiftIso(asOf, 1) ? name.split(" ")[0] : name} ${time}`;
};

/** First 4 and last 4 hex, as on the photo view. */
export const shortHash = (sha: string): string =>
  sha.length > 12
    ? `${sha.slice(0, 4)} ${sha.slice(4, 8)} … ${sha.slice(-4)}`
    : sha;

const DATE_ERROR_COPY: Record<DateErrorCode, string> = {
  after_max: "Too late",
  before_min: "Too early",
  locked: "Set by law",
  step_done: "Already done",
  not_in_service: "CodeClear is not handling this",
  unknown_step: "Not a step",
  sold: "Building sold. It comes off the LL33 list",
  bad_date: "Not a valid date",
};

type DayBounds = { minOn: string | null; maxOn: string | null };

/** The inline refusal before a dates write, or null. Bounds only; no date waits on another (Leo 10-16). */
export const dateRefusal = (
  day: string,
  bounds: DayBounds,
): DateErrorCode | null => {
  // `YYYY-MM-DD` strings compare in day order.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return "bad_date";
  if (bounds.maxOn && day > bounds.maxOn) return "after_max";
  if (bounds.minOn && day < bounds.minOn) return "before_min";
  return null;
};

/** Inline editor error; bounds name the date the server allows. */
export const dateErrorText = (
  code: DateErrorCode,
  bounds: DayBounds,
  asOf: string,
): string => {
  if (code === "after_max" && bounds.maxOn)
    return `Latest is ${dayLabel(bounds.maxOn, asOf)}`;
  if (code === "before_min" && bounds.minOn)
    return `Earliest is ${dayLabel(bounds.minOn, asOf)}`;
  return DATE_ERROR_COPY[code] ?? DATE_ERROR_COPY.bad_date;
};

/** Leo 10-07: photo in, waiting on CodeClear's Approve. Neutral, never risk red. */
export const CHECKING_COPY = "Photo in, CodeClear is checking";

/** F27: the served estimate pips below the door rows; null with none. */
export const estimateText = (line: Ll33StatusLine): string | null => {
  const n = pipCount(line, "estimate");
  return n > 0 ? `${n} more by the lot estimate, not counted` : null;
};

export type NotCountedReason = NonNullable<Ll33Photo["notCountedReason"]>;

/** Keyed by the served union: a new reason fails tsc, never reads a fallback. */
export const NOT_COUNTED_COPY: Record<NotCountedReason, string> = {
  rejected: "Not accepted",
  before_label: "Sent before the label was ready",
  link_revoked: "Link was stopped",
  door_retired: "Door removed",
  unassigned: "Not matched to a door",
  closeup: "Close-up: counts with the door photo",
  reopened: "Earlier photo, label gone since",
};

/** `constants/ll33.py` kinds. Unknown kinds drop. */
export const ACTIVITY_COPY: Record<string, string> = {
  AUTHORIZATION_RECORDED: "Authorized CodeClear",
  LABEL_DOWNLOADED: "Label downloaded",
  DOORS_ESTIMATED: "Door estimate set",
  DOOR_CONFIRMED: "Door confirmed",
  DOOR_RELABELED: "Door renamed",
  DOOR_RETIRED: "Door removed",
  LINK_ISSUED: "Door link issued",
  LINK_REVOKED: "Door link stopped",
  PROOF_RECEIVED: "Photo in",
  PROOF_ASSIGNED: "Photo matched to door",
  PROOF_APPROVED: "Photo approved",
  PROOF_REJECTED: "Photo not accepted",
  STEP_DATE_SET: "Date changed",
  DOORS_COMPLETE: "All doors confirmed",
};

/** The signed line, board f-states: signer, title, capacity, owner, BINs, version. */
const grantText = (g: NonNullable<Ll33Card["activity"][number]["grant"]>) =>
  `${[g.signer, g.title, g.capacity.toLowerCase()].filter(Boolean).join(", ")}, authorized CodeClear for ${g.owner.replace(/\.$/, "")}. ${plural(g.binCount, "BIN")}. ${g.textVersion}. Receipt emailed.`;

/** "Post date set to Oct 20": STEP_DATE_SET names its step and date. */
export const activityText = (
  a: Ll33Card["activity"][number],
  asOf: string,
): string => {
  if (a.kind === "STEP_DATE_SET" && a.step && a.dueOn)
    return `${STEP_LABEL[a.step]} date set to ${dayLabel(a.dueOn, asOf)}`;
  if (a.kind === "AUTHORIZATION_RECORDED" && a.grant) return grantText(a.grant);
  return ACTIVITY_COPY[a.kind] ?? "";
};
